import React from "react";
import { CCard, CCardBody } from "@coreui/react";
import { ColoredValue } from "@src/components/ColoredValue/ColoredValue";
import type * as T from "@src/types";

interface Props {
  metrics: T.PortfolioTypes.PortfolioMetrics;
}

interface MetricItemProps {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  className?: string;
  truncate?: boolean;
}

const MetricItem: React.FC<MetricItemProps> = ({ label, value, sub, className, truncate }) => (
  <div className={`PortfolioMetrics__item ${className ?? ""}`}>
    <div className="PortfolioMetrics__label">{label}</div>
    <div className={`PortfolioMetrics__value ${truncate ? "text-truncate" : ""}`}>{value}</div>
    {sub && <div className="PortfolioMetrics__sub">{sub}</div>}
  </div>
);

export const PortfolioMetrics: React.FC<Props> = ({ metrics }) => {
  return (
    <CCard className="PortfolioMetrics mb-3">
      <CCardBody className="p-0">
        <div className="PortfolioMetrics__row">
          <MetricItem
            label="Portfolio Value"
            value={
              <ColoredValue value={metrics.totalCurrentCapital} type="currency" decimals={2} />
            }
            sub={
              <>
                Initial:{" "}
                <ColoredValue value={metrics.totalInitialCapital} type="currency" decimals={2} />
              </>
            }
          />
          <MetricItem
            label="Total PnL"
            value={<ColoredValue value={metrics.totalPnl} threshold={0} type="currency" />}
            sub={<ColoredValue value={metrics.totalPnlPct} threshold={0} type="percentage" />}
          />
          <MetricItem
            label="Active"
            value={`${metrics.activeStrategiesCount} / ${metrics.totalStrategiesCount}`}
          />
          <MetricItem
            label="Positions"
            value={metrics.totalPositions}
            sub={
              <>
                WR{" "}
                <ColoredValue value={metrics.avgWinRate * 100} threshold={50} type="percentage" />
              </>
            }
          />
          <MetricItem
            label="Best"
            truncate
            className="PortfolioMetrics__item--wide"
            value={
              <span title={metrics.bestPerformingStrategy?.name}>
                {metrics.bestPerformingStrategy?.name ?? "—"}
              </span>
            }
            sub={
              metrics.bestPerformingStrategy ? (
                <ColoredValue
                  value={metrics.bestPerformingStrategy.pnlPct}
                  threshold={0}
                  type="percentage"
                />
              ) : undefined
            }
          />
          <MetricItem
            label="Worst"
            truncate
            className="PortfolioMetrics__item--wide"
            value={
              <span title={metrics.worstPerformingStrategy?.name}>
                {metrics.worstPerformingStrategy?.name ?? "—"}
              </span>
            }
            sub={
              metrics.worstPerformingStrategy ? (
                <ColoredValue
                  value={metrics.worstPerformingStrategy.pnlPct}
                  threshold={0}
                  type="percentage"
                />
              ) : undefined
            }
          />
          <MetricItem
            label="Max DD"
            value={
              <ColoredValue
                value={metrics.maxPortfolioDrawdownPct}
                forceColor="loss"
                type="percentage"
              />
            }
            sub={
              <>
                Sharpe <ColoredValue value={metrics.avgSharpeRatio} type="number" decimals={2} />
              </>
            }
          />
        </div>
      </CCardBody>
    </CCard>
  );
};
